/**
 * All text in the app goes through `t`. English and Dutch; the locale follows the device and can be
 * overridden. React re-renders on a locale change through `useLocale` (ui/hooks).
 */
import {
  ITEMS,
  KIND_KEYS,
  MACHINES,
  type Cost,
  type Kind,
  type MachineType,
} from '@bramvasterd/terrella-rules';

import { en, type MessageKey } from './en';
import { nl } from './nl';
import type { Locale, Message, Params } from './types';

export type { Locale, MessageKey, Params };

const tables: Record<Locale, Record<MessageKey, Message>> = { en, nl };
const listeners = new Set<(locale: Locale) => void>();
let locale: Locale = 'en';

export function detectLocale(languages: readonly string[] = navigator.languages): Locale {
  for (const language of languages) if (language.toLowerCase().startsWith('nl')) return 'nl';
  return 'en';
}

export const getLocale = (): Locale => locale;

export function setLocale(next: Locale): void {
  if (next === locale) return;
  locale = next;
  document.documentElement.lang = next;
  for (const listener of [...listeners]) listener(next);
}

export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function fill(message: Message, params: Params | undefined): string {
  if (typeof message === 'function') return message(params ?? {});
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function t(key: MessageKey, params?: Params): string {
  return fill(tables[locale][key], params);
}

/** The name of a tile kind, for labels and refusals. */
export const kindName = (kind: Kind): string => t(`kind.${KIND_KEYS[kind]}`);

/** A machine's own name, "Crystal resonator"; `many` for more than one, lower case: "crystal resonators". */
export const machineName = (type: MachineType, many = false): string =>
  t(many ? `machine.${type}-many` : `machine.${type}`);

/** The ground a machine type stands on, in words: "ore", or for a charge "solid rock". */
export function groundName(type: MachineType): string {
  const spec = MACHINES[type];
  return spec.role === 'extractor' ? kindName(spec.ground).toLowerCase() : t('kind.rock');
}

/** A number as the locale writes it, at most two decimals: "2.5", "2,5". */
export const numberText = (n: number): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);

/**
 * An item's name: "Wood", "Stone", "Crystal"; a block's, "Stone block", and for more than one of a
 * countable item its plural, "Stone blocks" (a mass noun like stone has none and stays).
 */
export function resourceName(resource: string, n = 1): string {
  const many = `resource.${resource}-many`;
  return t((n !== 1 && many in en ? many : `resource.${resource}`) as MessageKey);
}

/** "3 wood, 2 stone and 1 crystal". */
export function costText(cost: Cost): string {
  const parts = ITEMS.filter((r) => (cost[r] ?? 0) > 0).map((r) =>
    t('resource.amount', { n: cost[r] ?? 0, resource: resourceName(r, cost[r]).toLowerCase() }),
  );
  if (parts.length === 0) return t('resource.nothing');
  if (parts.length === 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} ${t('resource.and')} ${parts[parts.length - 1] ?? ''}`;
}

/** "4:12", "0:07": a short countdown in minutes and seconds, rounded up to the second. */
export function countdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

/** "4 h", "3 h 20 min", "12 min", "7 s": a duration from now, rounded up to the minute, or the second under a minute. */
export function duration(ms: number): string {
  if (ms < 60_000) return t('time.s', { n: Math.max(1, Math.ceil(ms / 1000)) });
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return t('time.min', { n: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? t('time.hm', { h, m }) : t('time.h', { h });
}

type RefusalLike = { code: string } & Record<string, unknown>;

/** A refusal's details as words for its sentence: kinds and resources by name. */
function refusalParams(refusal: RefusalLike): Params {
  const params: Params = {};
  for (const [name, value] of Object.entries(refusal)) {
    if (name === 'code') continue;
    if ((name === 'ground' || name === 'kind') && typeof value === 'number') {
      params[name] = kindName(value as Kind).toLowerCase();
    } else if (name === 'type' && typeof value === 'string' && value in MACHINES) {
      // a machine type: where it stands ("Must be placed on ore"), or its plural ("2 stonecutters")
      params['ground'] = groundName(value as MachineType);
      params['names'] = machineName(value as MachineType, true);
    } else if ((name === 'resource' || name === 'item') && typeof value === 'string') {
      params['resource'] = resourceName(
        value,
        name === 'item' ? Number(refusal['need']) : 1,
      ).toLowerCase();
    } else if (typeof value === 'string' || typeof value === 'number') params[name] = value;
  }
  return params;
}

/** A refusal from the rules or the server, as a sentence. Unknown codes get a generic line. */
export function refusalText(refusal: RefusalLike): string {
  const key = `refused.${refusal.code}`;
  if (!(key in en)) return t('refused.unknown');
  return t(key as MessageKey, refusalParams(refusal));
}

/** Why an action can't happen now, short, for its button ("Must be placed on ore", "You need 3 more stone"). */
export function reasonText(refusal: RefusalLike): string {
  const key = `reason.${refusal.code}`;
  return key in en ? t(key as MessageKey, refusalParams(refusal)) : refusalText(refusal);
}
